import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, Camera, CheckCircle, FileText, AlertCircle, X, Clock, Save, Loader2, Lock, Unlock, Pencil, Mail, MapPin, Calendar, FileCheck } from 'lucide-react';
import { ProfileForm, type ProfileFormHandle } from '../components/Profile';
import { DocumentUpload, type UploadedDocument, type DocumentType, type IdDocumentChoice } from '../components/DocumentUpload/DocumentUpload';
import { ProfileSubmissionFlow } from '../components/ProfileSubmissionFlow';
import { StudentProfileView } from '../components/StudentProfileView';
import { StaffProfileView } from '../components/StaffProfileView';
import { StudentProfileForm, type StudentProfileFormHandle, type StudentProfileFormData, type StudentUploadedDocument, type CampusInfo } from '@/features/etudiant';
import { ParentProfileForm, type ParentProfileFormHandle, type ParentProfileFormData } from '@/features/parent';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { CommonProfileFields } from '@/types/profile.types';
import { UserRole } from '@/types/global.types';
import {
  fetchProfile,
  updateProfile,
  saveStaffProfileProgress,
  updateStudentProfile,
  saveStudentProfileProgress,
  getCachedProfile,
  isProfileSubmitted,
  unlockProfile,
  type UserProfile,
  type UserCampusAffiliation,
  type AcademicYearInfo,
  type StudentProfileData,
} from '@/services/profileService';
import { getValidationStatus, type ValidationStatus } from '@/services/validationService';
import {
  saveDocument,
  getAllDocuments,
  deleteDocument,
  getDocumentPreviewUrl,
  storedDocumentToFile,
} from '@/services/documentStorageService';
import { uploadDocument as uploadDocumentToServer, type StudentDocumentType } from '@/services/documentsService';
import { fetchDepartements, fetchGrades, fetchDiplomes, type NomenclatureDepartement, type NomenclatureGrade, type NomenclatureDiplome } from '@/services/nomenclatureService';
import { fetchCampuses, type Campus } from '@/services/etablissementService';
import { countries as countriesData } from '@/data/countries';

// Super Admin Profile Section Component - Simplified with inline edit
interface SuperAdminProfileSectionProps {
  nom: string;
  prenom: string;
  email: string;
  onSave: (data: { nom: string; prenom: string; email: string }) => Promise<void>;
  isSaving: boolean;
}

const SuperAdminProfileSection: React.FC<SuperAdminProfileSectionProps> = ({
  nom,
  prenom,
  email,
  onSave,
  isSaving,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editNom, setEditNom] = useState(nom);
  const [editPrenom, setEditPrenom] = useState(prenom);
  const [editEmail, setEditEmail] = useState(email);

  // Sync local state when props change
  useEffect(() => {
    setEditNom(nom);
    setEditPrenom(prenom);
    setEditEmail(email);
  }, [nom, prenom, email]);

  const handleSave = async () => {
    await onSave({ nom: editNom, prenom: editPrenom, email: editEmail });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditNom(nom);
    setEditPrenom(prenom);
    setEditEmail(email);
    setIsEditing(false);
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <User className="w-5 h-5 text-primary-600" />
          Informations du profil
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Modifier
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={editNom}
                onChange={(e) => setEditNom(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                placeholder="NOM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
              <input
                type="text"
                value={editPrenom}
                onChange={(e) => setEditPrenom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Prénom"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="email@exemple.com"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={isSaving || !editNom.trim() || !editPrenom.trim() || !isValidEmail(editEmail)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Enregistrer
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <X className="w-3.5 h-3.5" />
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Name display */}
          <div>
            <span className="text-sm text-gray-500">Nom complet</span>
            <p className="text-gray-900 font-medium">{prenom} {nom}</p>
          </div>

          {/* Email display */}
          <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="text-gray-900">{email}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ProfilePage: React.FC = () => {
  const { user, updateUserProfile, updateProfileSubmissionStatus, updateProfileCompleted } = useAuth();
  const formRef = useRef<ProfileFormHandle>(null);
  const studentFormRef = useRef<StudentProfileFormHandle>(null);
  const parentFormRef = useRef<ParentProfileFormHandle>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [isSubmitting] = useState(false); // Submission state managed by ProfileSubmissionFlow
  const [, setIsLoadingProfile] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false); // Track if profile has been loaded from API
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [progressSaved, setProgressSaved] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [selectedIdDocType, setSelectedIdDocType] = useState<IdDocumentChoice>('CIN');
  const [error, setError] = useState<string | null>(null);
  const [isProfileLocked, setIsProfileLocked] = useState(false);
  const [isParentCreatedByStaff, setIsParentCreatedByStaff] = useState(false); // For parent/tuteur accounts created by staff
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [studentProfileData, setStudentProfileData] = useState<Partial<StudentProfileFormData>>({});
  const [parentProfileData, setParentProfileData] = useState<Partial<ParentProfileFormData>>({});
  const [studentCampus, setStudentCampus] = useState<CampusInfo | null>(null);
  const [staffCampuses, setStaffCampuses] = useState<UserCampusAffiliation[]>([]);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(null);
  const [userAcademicYear, setUserAcademicYear] = useState<AcademicYearInfo | null>(null);
  const [studentAcademicYear, setStudentAcademicYear] = useState<AcademicYearInfo | null>(null);
  const [studentCode, setStudentCode] = useState<string | null>(null);
  const [staffCode, setStaffCode] = useState<string | null>(null);

  // Tenant info for professors
  const [currentAcademicYear, setCurrentAcademicYear] = useState<string | null>(null);
  const [engagementTemplateUrl, setEngagementTemplateUrl] = useState<string | null>(null);

  // Departements for professors and staff who also teach
  const [departements, setDepartements] = useState<NomenclatureDepartement[]>([]);
  // Grades for professors and staff who also teach
  const [grades, setGrades] = useState<NomenclatureGrade[]>([]);
  // Diplomes for staff (to resolve diplomeId to name)
  const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);
  // Campuses for staff (to resolve payrollCampusId to name)
  const [campuses, setCampuses] = useState<Campus[]>([]);

  // Physical documents tracking state (for DG)
  const [physicalDocs, setPhysicalDocs] = useState({
    physicalDocCin: false,
    physicalDocPassport: false,
    physicalDocCv: false,
    physicalDocContratTravail: false,
    physicalDocDiplome: false,
    physicalDocAttestationRib: false,
    physicalDocEngagementProfesseur: false,
    physicalDocReglementInterieur: false,
    physicalDocConsentement: false,
  });

  // Profile data state (initial values)
  const [profileData, setProfileData] = useState<Partial<CommonProfileFields>>({
    email: user?.email || '',
    nom: user?.lastName || '',
    prenom: user?.firstName || '',
    paysOrigine: 'MA',
    sexe: 'M',
    handicap: 'N',
  });

  // Roles that can edit their OWN locked profile (bypass validation)
  // Only DG and SUPER_ADMIN - they don't need validation for their own profile
  // Note: DP CAN edit OTHER users' locked profiles (on admin pages), but must follow validation for their OWN profile
  const canEditLockedProfile = user?.role === UserRole.DIRECTOR_GENERAL ||
    user?.role === UserRole.SUPER_ADMIN;

  // Roles that should start in view mode after saving their profile (only DG - doesn't need validation)
  const shouldStartInViewModeAfterSave = user?.role === UserRole.DIRECTOR_GENERAL;

  // Map backend profile to form data (only include defined values)
  const mapProfileToFormData = (profile: UserProfile): Partial<CommonProfileFields> => {
    const result: Partial<CommonProfileFields> = {};

    // Only add values that are defined (not null/undefined)
    if (profile.nom) result.nom = profile.nom;
    if (profile.prenom) result.prenom = profile.prenom;
    if (profile.nomArabe) result.nomArabe = profile.nomArabe;
    if (profile.prenomArabe) result.prenomArabe = profile.prenomArabe;
    if (profile.cnie) result.cnie = profile.cnie;
    if (profile.passport) result.passport = profile.passport;
    if (profile.paysOrigine) result.paysOrigine = profile.paysOrigine;
    if (profile.email) result.email = profile.email;
    if (profile.telephone) result.telephone = profile.telephone;
    if (profile.phoneCountry) result.phoneCountry = profile.phoneCountry;
    if (profile.sexe) result.sexe = profile.sexe as 'M' | 'F';
    if (profile.dateNaissance) result.dateNaissance = profile.dateNaissance;
    if (profile.handicap) result.handicap = profile.handicap as 'N' | 'A' | 'M' | 'V' | 'AM' | 'AV' | 'MV' | 'T' | 'XX';
    if (profile.adresse) result.adresse = profile.adresse;
    if (profile.urgenceNom) result.urgenceNom = profile.urgenceNom;
    if (profile.urgenceRelation) result.urgenceRelation = profile.urgenceRelation;
    if (profile.urgencePhoneCountry) result.urgencePhoneCountry = profile.urgencePhoneCountry;
    if (profile.urgenceNumero) result.urgenceNumero = profile.urgenceNumero;
    if (profile.urgenceAdresse) result.urgenceAdresse = profile.urgenceAdresse;
    if (profile.rib) result.rib = profile.rib;
    if (profile.salaireMensuelNet) result.salaireMensuelNet = profile.salaireMensuelNet;
    if (profile.fonction) result.fonction = profile.fonction;
    if (profile.diplomeId) result.diplomeId = profile.diplomeId;
    if (profile.dateRecrutement) result.dateRecrutement = profile.dateRecrutement;
    // Professor E-DEP fields
    if (profile.statutProfesseur) (result as Record<string, unknown>).statutProfesseur = profile.statutProfesseur;
    if (profile.departementAttacheId) (result as Record<string, unknown>).departementAttacheId = profile.departementAttacheId;
    if (profile.estOriginaireEtablissementPublic) (result as Record<string, unknown>).estOriginaireEtablissementPublic = profile.estOriginaireEtablissementPublic;
    if (profile.etablissementOrigine) (result as Record<string, unknown>).etablissementOrigine = profile.etablissementOrigine;
    if (profile.gradeId) (result as Record<string, unknown>).gradeId = profile.gradeId;
    if (profile.specialite) (result as Record<string, unknown>).specialite = profile.specialite;
    if (profile.matieresEnseignees) (result as Record<string, unknown>).matieresEnseignees = profile.matieresEnseignees;
    if (profile.tauxHoraire) (result as Record<string, unknown>).tauxHoraire = profile.tauxHoraire;
    // Staff who also teach
    if (profile.isAlsoProfessor !== undefined) (result as Record<string, unknown>).isAlsoProfessor = profile.isAlsoProfessor;

    return result;
  };

  // Map form data to backend profile (filter out empty strings to avoid validation errors)
  const mapFormDataToProfile = (data: CommonProfileFields | Partial<CommonProfileFields>): UserProfile => {
    // Helper to convert empty strings to undefined
    const nonEmpty = (val: string | undefined): string | undefined => (val && val.trim() !== '' ? val : undefined);

    return {
      // Identity
      nom: nonEmpty(data.nom),
      prenom: nonEmpty(data.prenom),
      nomArabe: nonEmpty(data.nomArabe),
      prenomArabe: nonEmpty(data.prenomArabe),
      cnie: nonEmpty(data.cnie),
      passport: nonEmpty(data.passport),
      paysOrigine: nonEmpty(data.paysOrigine),
      // Personal info
      email: nonEmpty(data.email),
      telephone: nonEmpty(data.telephone),
      phoneCountry: nonEmpty(data.phoneCountry),
      sexe: nonEmpty(data.sexe),
      dateNaissance: nonEmpty(data.dateNaissance),
      handicap: nonEmpty(data.handicap),
      // Address
      adresse: nonEmpty(data.adresse),
      // Emergency contact
      urgenceNom: nonEmpty(data.urgenceNom),
      urgenceRelation: nonEmpty(data.urgenceRelation),
      urgencePhoneCountry: nonEmpty(data.urgencePhoneCountry),
      urgenceNumero: nonEmpty(data.urgenceNumero),
      urgenceAdresse: nonEmpty(data.urgenceAdresse),
      // Staff-specific professional info
      // Strip spaces from RIB and only send if non-empty (must be exactly 24 digits for validation)
      rib: data.rib && data.rib.trim() !== '' ? data.rib.replace(/\s/g, '') : undefined,
      // Remove all formatting (spaces, non-breaking spaces, dots, commas) from salary to store as plain number string
      salaireMensuelNet: data.salaireMensuelNet && data.salaireMensuelNet.trim() !== ''
        ? data.salaireMensuelNet.replace(/[\s\u00A0.,]/g, '')
        : undefined,
      fonction: nonEmpty(data.fonction),
      diplomeId: nonEmpty(data.diplomeId),
      dateRecrutement: nonEmpty(data.dateRecrutement),
      // Professor E-DEP fields
      statutProfesseur: nonEmpty((data as Record<string, unknown>).statutProfesseur as string | undefined),
      departementAttacheId: nonEmpty((data as Record<string, unknown>).departementAttacheId as string | undefined),
      estOriginaireEtablissementPublic: nonEmpty((data as Record<string, unknown>).estOriginaireEtablissementPublic as string | undefined),
      etablissementOrigine: nonEmpty((data as Record<string, unknown>).etablissementOrigine as string | undefined),
      gradeId: nonEmpty((data as Record<string, unknown>).gradeId as string | undefined),
      specialite: nonEmpty((data as Record<string, unknown>).specialite as string | undefined),
      matieresEnseignees: nonEmpty((data as Record<string, unknown>).matieresEnseignees as string | undefined),
      tauxHoraire: nonEmpty((data as Record<string, unknown>).tauxHoraire as string | undefined),
      // Staff who also teach
      isAlsoProfessor: (data as Record<string, unknown>).isAlsoProfessor as boolean | undefined,
      // Payroll campus
      payrollCampusId: nonEmpty((data as Record<string, unknown>).payrollCampusId as string | undefined),
    };
  };

  // Load stored documents from IndexedDB
  const loadStoredDocuments = useCallback(async () => {
    try {
      const storedDocs = await getAllDocuments();
      const uploadedDocs: UploadedDocument[] = storedDocs.map(doc => {
        // Create a minimal File object that maintains the original size and mime type
        // The actual file content is already stored in IndexedDB as base64
        const blob = new Blob(['stored'], { type: doc.mimeType });
        const file = new File([blob], doc.fileName, { type: doc.mimeType });
        // Store original size as a property for reference
        Object.defineProperty(file, '_originalSize', { value: doc.size, writable: false });
        return {
          type: doc.type as DocumentType,
          file,
          previewUrl: getDocumentPreviewUrl(doc),
        };
      });
      setDocuments(uploadedDocs);
    } catch (err) {
      console.error('Error loading stored documents:', err);
    }
  }, []);

  // Load profile data on mount
  useEffect(() => {
    const loadProfile = async () => {
      setIsLoadingProfile(true);

      try {
        // Check if profile is locked
        const locked = isProfileSubmitted();
        setIsProfileLocked(locked);

        // Determine initial edit mode:
        // - For DG: Start in view mode if profile was already saved (profileCompleted = true) - DG doesn't need validation
        // - For others: Start in edit mode if profile is not locked (or user can edit locked profiles)
        if (shouldStartInViewModeAfterSave) {
          // DG: Start in view mode if profile has been completed, edit mode otherwise
          setIsEditing(!user?.profileCompleted);
        } else {
          // Other roles: Start in edit mode only if profile is not locked (or user can edit locked profiles)
          setIsEditing(!locked || canEditLockedProfile);
        }

        // Fetch validation status from backend
        try {
          const status = await getValidationStatus();
          setValidationStatus(status);
          // If backend says profile is pending/approved, mark as locked
          if (status.status === 'PENDING_VALIDATION' || status.status === 'APPROVED') {
            setIsProfileLocked(true);
            // For DG, keep their view mode logic; for others, check canEditLockedProfile
            if (!shouldStartInViewModeAfterSave) {
              setIsEditing(canEditLockedProfile);
            }
          }
        } catch (err) {
          // Not critical if validation status fetch fails
          console.warn('Could not fetch validation status:', err);
        }

        // First, try to get cached profile for instant display
        const cached = getCachedProfile();

        if (cached) {
          const mappedData = mapProfileToFormData(cached);
          setProfileData(prev => ({
            ...prev,
            ...mappedData,
          }));

          // Sync profile name/email with auth store (updates Header)
          if (cached.prenom || cached.nom || cached.email) {
            updateUserProfile({
              firstName: cached.prenom,
              lastName: cached.nom,
              email: cached.email,
            });
          }
        }

        // Load stored documents from IndexedDB
        await loadStoredDocuments();

        // Then fetch from API/storage
        const profileResponse = await fetchProfile();

        if (profileResponse) {
          // Load documents from backend API response (uploaded by staff)
          // These take precedence over IndexedDB documents as they're server-confirmed
          if (profileResponse.profile?.documents && Array.isArray(profileResponse.profile.documents)) {
            const backendDocs: UploadedDocument[] = profileResponse.profile.documents.map((doc: {
              documentType: string;
              fileName: string;
              fileUrl: string;
              fileSize?: number;
              mimeType?: string;
            }) => {
              // Create a placeholder file object for backend documents
              const blob = new Blob(['backend'], { type: doc.mimeType || 'application/octet-stream' });
              const file = new File([blob], doc.fileName, { type: doc.mimeType || 'application/octet-stream' });
              // Store original size and mark as backend document
              Object.defineProperty(file, '_originalSize', { value: doc.fileSize || 0, writable: false });
              Object.defineProperty(file, '_isBackendDocument', { value: true, writable: false });
              return {
                type: doc.documentType as DocumentType,
                file,
                // Use the backend URL directly (served by the API)
                previewUrl: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${doc.fileUrl}`,
              };
            });

            // Merge with IndexedDB documents - backend docs take precedence
            setDocuments(prevDocs => {
              const backendTypes = new Set(backendDocs.map(d => d.type));
              // Keep local docs that don't exist in backend, then add all backend docs
              const localOnlyDocs = prevDocs.filter(d => !backendTypes.has(d.type));
              return [...localOnlyDocs, ...backendDocs];
            });
          }

          // Map profile data, but use firstName/lastName from user response
          const mappedData = profileResponse.profile
            ? mapProfileToFormData(profileResponse.profile)
            : {};

          // Merge with user-level data (firstName/lastName/phone/isAlsoProfessor/payrollCampusId come from User, not UserProfile)
          const newProfileData = {
            ...mappedData,
            // Use nom/prenom from profile if available, otherwise use firstName/lastName from user response
            nom: mappedData.nom || profileResponse.lastName || '',
            prenom: mappedData.prenom || profileResponse.firstName || '',
            email: mappedData.email || profileResponse.email || '',
            telephone: mappedData.telephone || profileResponse.phone || '',
            // isAlsoProfessor comes from User model (not UserProfile)
            isAlsoProfessor: profileResponse.isAlsoProfessor,
            // payrollCampusId comes from User model
            payrollCampusId: profileResponse.payrollCampusId,
          };
          setProfileData(newProfileData as Record<string, unknown>);

          // Mark profile as loaded from API
          setProfileLoaded(true);

          // Get tenant info for professors
          if (profileResponse.tenantInfo) {
            setCurrentAcademicYear(profileResponse.tenantInfo.currentAcademicYear || null);
            setEngagementTemplateUrl(profileResponse.tenantInfo.engagementTemplateUrl || null);
          }

          // Get staff campus affiliations (for all staff except students and parents)
          if (profileResponse.userCampuses && profileResponse.userCampuses.length > 0) {
            setStaffCampuses(profileResponse.userCampuses);
          }

          // Get user's academic year assignment (for staff)
          if (profileResponse.academicYear) {
            setUserAcademicYear(profileResponse.academicYear);
          }

          // Get staff code for staff users
          if (profileResponse.staffCode) {
            setStaffCode(profileResponse.staffCode);
          }

          // Load physical documents tracking state for DG
          if (profileResponse.profile) {
            setPhysicalDocs({
              physicalDocCin: profileResponse.profile.physicalDocCin ?? false,
              physicalDocPassport: profileResponse.profile.physicalDocPassport ?? false,
              physicalDocCv: profileResponse.profile.physicalDocCv ?? false,
              physicalDocContratTravail: profileResponse.profile.physicalDocContratTravail ?? false,
              physicalDocDiplome: profileResponse.profile.physicalDocDiplome ?? false,
              physicalDocAttestationRib: profileResponse.profile.physicalDocAttestationRib ?? false,
              physicalDocEngagementProfesseur: profileResponse.profile.physicalDocEngagementProfesseur ?? false,
              physicalDocReglementInterieur: profileResponse.profile.physicalDocReglementInterieur ?? false,
              physicalDocConsentement: profileResponse.profile.physicalDocConsentement ?? false,
            });
          }

          // Map student data for students (from Student table + UserProfile table)
          if (profileResponse.role === 'STUDENT' && profileResponse.student) {
            const student = profileResponse.student;
            const profile = profileResponse.profile;

            // Helper function to convert gender from backend (MALE/FEMALE) to form (M/F)
            const mapGender = (gender?: string): 'M' | 'F' => {
              if (gender === 'MALE' || gender === 'M') return 'M';
              if (gender === 'FEMALE' || gender === 'F') return 'F';
              return 'M'; // default
            };

            // Helper function to convert ISO date to YYYY-MM-DD format
            const formatDate = (dateStr?: string): string => {
              if (!dateStr) return '';
              try {
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return '';
                return date.toISOString().split('T')[0];
              } catch {
                return '';
              }
            };

            // Helper function to map nationality/country name to country code
            // Uses the countries data to find the code by name (case-insensitive)
            const mapNationalityToCode = (value?: string): string => {
              if (!value) return 'MA';

              // If value is already a valid country code, return it
              const existingCode = countriesData.find(c => c.code === value);
              if (existingCode) return value;

              // Try to find by French name (case-insensitive, with accent handling)
              const normalizedValue = value.toLowerCase().trim();
              const foundByName = countriesData.find(c =>
                c.nameFr.toLowerCase() === normalizedValue
              );
              if (foundByName) return foundByName.code;

              // Common nationality adjective mappings
              const nationalityMappings: Record<string, string> = {
                'marocain': 'MA',
                'marocaine': 'MA',
                'moroccan': 'MA',
                'français': 'FR',
                'française': 'FR',
                'french': 'FR',
              };
              const mappedCode = nationalityMappings[normalizedValue];
              if (mappedCode) return mappedCode;

              // Default to Morocco if nothing found
              console.warn(`[ProfilePage] Unknown country/nationality: "${value}", defaulting to MA`);
              return 'MA';
            };

            // Map backend student data to StudentProfileFormData format
            const mappedStudentData: Partial<StudentProfileFormData> = {
              // Identity - from User/Student tables
              nom: student.lastName || profileResponse.lastName || '',
              prenom: student.firstName || profileResponse.firstName || '',
              nomArabe: student.lastNameArabic || profile?.nomArabe || '',
              prenomArabe: student.firstNameArabic || profile?.prenomArabe || '',
              cnie: student.cnie || profile?.cnie || '',
              passport: student.passportNumber || profile?.passport || '',
              sexe: mapGender(student.gender || profile?.sexe),
              dateNaissance: formatDate(student.dateOfBirth) || profile?.dateNaissance || '',
              // Prioritize profile.paysOrigine (from bulk import) over student.nationality
              // Convert to country code if stored as name (e.g., "Comores" -> "CO47")
              paysOrigine: mapNationalityToCode(profile?.paysOrigine || student.nationality),
              handicap: (profile?.handicap || 'N') as 'N' | 'A' | 'M' | 'V' | 'AM' | 'AV' | 'MV' | 'T' | 'XX',

              // Contact - from User/Student tables
              email: student.email || profileResponse.email || '',
              telephone: student.phone || profileResponse.phone || '',
              adresse: student.address !== 'Non renseigné' ? (student.address || '') : (profile?.adresse || ''),

              // Emergency contact - from Student table or UserProfile
              urgenceNom: student.emergencyContactName || profile?.urgenceNom || '',
              urgenceRelation: student.emergencyContactRelation || profile?.urgenceRelation || '',
              urgenceNumero: student.emergencyContactPhone || profile?.urgenceNumero || '',
              urgenceAdresse: profile?.urgenceAdresse || '',

              // Academic - from Student table
              numeroMassarCNE: student.numeroMassarCNE || '',
              diplomeInscrit: student.diplomeInscrit || '',
              filiere: student.filiereInscrit || student.codeFiliereInscrit || '',
              niveauFormation: student.niveauFormation || 1,
              situation: (student.situation || 'N') as 'N' | 'R' | 'P',
              premiereAnneeInscription: student.premiereAnneeInscription || '',

              // Diploma - from Student table
              serieBaccalaureat: student.serieBaccalaureat || '',
              anneeObtentionBac: student.anneeObtentionBac || '',
              diplomeAcces: student.diplomeAcces || 'Baccalauréat',
              specialiteDiplomeAcces: student.specialiteDiplomeAcces || '',
              mentionDiplomeAcces: (student.mentionDiplomeAcces || 'ns') as 'p' | 'ab' | 'b' | 'tb' | 'e' | 'ns',
              lieuObtentionDiplomeAcces: student.lieuObtentionDiplomeAcces || '',

              // Status - from Student table
              resident: (student.resident || 'N') as 'O' | 'N',
              fonctionnaire: (student.fonctionnaire || 'N') as 'O' | 'N',
              pourcentageBourse: student.pourcentageBourse || 0,
              mobilite: (student.mobilite || 'N') as 'O' | 'N',
            };

            console.log('Mapped student data:', mappedStudentData);
            setStudentProfileData(mappedStudentData);

            // Extract campus info if available
            if (student.campus) {
              setStudentCampus({
                id: student.campus.id,
                name: student.campus.name,
                city: student.campus.city || '',
              });
            }

            // Extract academic year info for student
            if (student.academicYear) {
              setStudentAcademicYear(student.academicYear);
            }

            // Store student code for display
            if (student.studentCode) {
              setStudentCode(student.studentCode);
            }
          }

          // Map parent data for parents (from User table + UserProfile table)
          if (profileResponse.role === 'PARENT') {
            const profile = profileResponse.profile;

            // Check if parent was created by staff (profile should be view-only)
            if (profileResponse.isCreatedByStaff) {
              setIsParentCreatedByStaff(true);
            }

            // Map backend data to ParentProfileFormData format
            const mappedParentData: Partial<ParentProfileFormData> = {
              // Identity - from User table
              nom: profileResponse.lastName || '',
              prenom: profileResponse.firstName || '',
              // Identity Arabic - from UserProfile
              nomArabe: profile?.nomArabe || '',
              prenomArabe: profile?.prenomArabe || '',
              // Personal info - from UserProfile
              sexe: (profile?.sexe || 'M') as 'M' | 'F',
              // Contact - from User/UserProfile
              email: profileResponse.email || '',
              telephone: profileResponse.phone || '',
              phoneCountry: profile?.phoneCountry || 'MA',
              // Address - from UserProfile
              adresse: profile?.adresse || '',
              // Relation - from UserProfile
              relationAvecEtudiant: profile?.relationAvecEtudiant || '',
              relationPrecision: profile?.relationPrecision || '',
            };

            console.log('Mapped parent data:', mappedParentData);
            setParentProfileData(mappedParentData);
          }
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, [loadStoredDocuments, canEditLockedProfile, updateUserProfile, shouldStartInViewModeAfterSave, user?.profileCompleted]);

  // Determine if user is a student (CV not required)
  const isStudent = user?.role === UserRole.STUDENT;

  // Determine if user is a parent/guardian
  const isParent = user?.role === UserRole.PARENT;

  // Determine if user is super admin (simplified profile form)
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  // Determine if user is staff (needs professional documents)
  const isStaff = user?.role === UserRole.SUPER_ADMIN ||
    user?.role === UserRole.DIRECTOR_GENERAL ||
    user?.role === UserRole.PEDAGOGICAL_DIRECTOR ||
    user?.role === UserRole.ACADEMIC_COORDINATOR ||
    user?.role === UserRole.FINANCIAL_MANAGER ||
    user?.role === UserRole.MARKETING_MANAGER ||
    user?.role === UserRole.COMMERCIAL_MANAGER ||
    user?.role === UserRole.PROGRAM_HEAD ||
    user?.role === UserRole.BRANCH_MANAGER ||
    user?.role === UserRole.TEACHER;

  // Determine if user is a professor
  const isProfesseur = user?.role === UserRole.TEACHER;

  // Staff roles who can also teach (for isAlsoProfessor logic)
  const staffRolesWhoCanTeach = [
    UserRole.DIRECTOR_GENERAL,
    UserRole.PEDAGOGICAL_DIRECTOR,
    UserRole.ACADEMIC_COORDINATOR,
    UserRole.FINANCIAL_MANAGER,
    UserRole.MARKETING_MANAGER,
    UserRole.COMMERCIAL_MANAGER,
    UserRole.PROGRAM_HEAD,
    UserRole.BRANCH_MANAGER,
  ];
  const canAlsoBeProfesor = user?.role && staffRolesWhoCanTeach.includes(user.role);

  // Load departements, grades, diplomes for professors and staff who can also teach
  // Also load campuses for all staff (to resolve payrollCampusId)
  useEffect(() => {
    const loadNomenclatures = async () => {
      try {
        // Get auth token from localStorage
        const token = localStorage.getItem('nexedu_access_token');
        if (token) {
          // Load diplomes and campuses for all staff
          if (isStaff) {
            const [diplomesData, campusesData] = await Promise.all([
              fetchDiplomes(true),
              fetchCampuses(token, true),
            ]);
            if (diplomesData && diplomesData.length > 0) {
              setDiplomes(diplomesData);
            }
            if (campusesData && campusesData.length > 0) {
              setCampuses(campusesData);
            }
          }

          // Only load departements and grades if user is professor or can be a professor
          if (isProfesseur || canAlsoBeProfesor) {
            const [departementsData, gradesData] = await Promise.all([
              fetchDepartements(token, true),
              fetchGrades(true),
            ]);
            if (departementsData && departementsData.length > 0) {
              setDepartements(departementsData);
            }
            if (gradesData && gradesData.length > 0) {
              setGrades(gradesData);
            }
          }
        }
      } catch (error) {
        console.error('Error loading nomenclatures:', error);
      }
    };
    loadNomenclatures();
  }, [isProfesseur, canAlsoBeProfesor, isStaff]);

  // Professor-specific states for document requirements (updated in real-time from form)
  const [professorStatus, setProfessorStatus] = useState<string | null>(null);
  const [fromPublicInstitution, setFromPublicInstitution] = useState<string | null>(null);
  // Local state for isAlsoProfessor (updated in real-time from form)
  const [localIsAlsoProfessor, setLocalIsAlsoProfessor] = useState<boolean | null>(null);

  // isAlsoProfessor state - use local state if set, otherwise from profile data
  const isAlsoProfessor = localIsAlsoProfessor ?? (profileData as Record<string, unknown>)?.isAlsoProfessor === true;

  // Determine if user should see professor documents:
  // - TEACHER role always sees them
  // - Staff who can teach AND have isAlsoProfessor=true also see them
  const showProfessorDocuments = isProfesseur || (canAlsoBeProfesor && isAlsoProfessor);

  // Determine professor status - use local state if set, otherwise from profile data
  const isVacataire = (professorStatus ?? (profileData as Record<string, unknown>)?.statutProfesseur) === 'V';
  const isFromPublicInstitution = (fromPublicInstitution ?? (profileData as Record<string, unknown>)?.estOriginaireEtablissementPublic) === 'O';

  // Callback to update professor status from form in real-time
  const handleProfessorFieldsChange = useCallback((statutProfesseur?: string, estOriginaireEtablissementPublic?: string) => {
    if (statutProfesseur !== undefined) {
      setProfessorStatus(statutProfesseur || null);
    }
    if (estOriginaireEtablissementPublic !== undefined) {
      setFromPublicInstitution(estOriginaireEtablissementPublic || null);
    }
  }, []);

  // Callback to update isAlsoProfessor from form in real-time
  // NOTE: We intentionally do NOT update profileData here to avoid form reset!
  // The form manages its own state via react-hook-form, and profileData is only updated on save.
  const handleIsAlsoProfessorChange = useCallback((value: boolean) => {
    setLocalIsAlsoProfessor(value);
    // DO NOT call setProfileData here - it triggers the form useEffect which resets the form
    // and wipes all the user's input. The value will be saved when the user clicks save.
  }, []);

  // Roles that can only save (not submit) - Only DG (has no supervisor to submit to)
  const canOnlySave = user?.role === UserRole.DIRECTOR_GENERAL;

  // Roles that must submit their profile (cannot just save)
  // Étudiant, Responsable financier, Responsable marketing, Responsable commercial, Staff administratif, Professeur, Parent, Coordinateur, Directeur Pédagogique
  const mustSubmitProfile = user?.role === UserRole.STUDENT ||
    user?.role === UserRole.ACADEMIC_COORDINATOR ||
    user?.role === UserRole.FINANCIAL_MANAGER ||
    user?.role === UserRole.MARKETING_MANAGER ||
    user?.role === UserRole.COMMERCIAL_MANAGER ||
    user?.role === UserRole.BRANCH_MANAGER ||
    user?.role === UserRole.TEACHER ||
    user?.role === UserRole.PARENT ||
    user?.role === UserRole.PROGRAM_HEAD ||
    user?.role === UserRole.PEDAGOGICAL_DIRECTOR;

  // Handle ID document type change from DocumentUpload component
  const handleIdDocumentTypeChange = (type: IdDocumentChoice) => {
    setSelectedIdDocType(type);
  };

  // Handle country change in profile form
  // Note: We intentionally do NOT update profileData here to avoid form reset loops.
  // The form manages its own state, and profileData is only updated on submit/save.
  const handleCountryChange = useCallback((_newCountry: string) => {
    // Previously this was updating profileData, but that caused the form to reset
    // because the hash-based change detection saw it as "new" data.
    // The form already manages paysOrigine internally via react-hook-form.
  }, []);

  const handleSubmit = async (data: CommonProfileFields) => {
    setIsSaving(true);
    setError(null);

    try {
      // Save to API/localStorage
      const profileToSave = mapFormDataToProfile(data);
      // Include physical documents for DG
      const dataWithPhysicalDocs = user?.role === UserRole.DIRECTOR_GENERAL
        ? { ...profileToSave, ...physicalDocs }
        : profileToSave;
      const result = await updateProfile(dataWithPhysicalDocs);

      // Update local state
      setProfileData(prev => ({ ...prev, ...data }));

      // Update user name/email in auth store (reflects in header)
      if (data.prenom || data.nom || data.email) {
        updateUserProfile({
          firstName: data.prenom,
          lastName: data.nom,
          email: data.email,
        });
      }

      // Update profile completed status in auth store (for access control)
      if (result.profileCompleted !== undefined) {
        updateProfileCompleted(result.profileCompleted);
      } else {
        // For DG, mark profile as completed after successful save (DG doesn't need validation)
        // This ensures they see view mode when returning to the profile page
        if (shouldStartInViewModeAfterSave) {
          updateProfileCompleted(true);
        }
      }

      setSaveSuccess(true);
      setIsEditing(false);

      // Reset success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProgress = async (data: Partial<CommonProfileFields>) => {
    setIsSavingProgress(true);
    setError(null);

    try {
      // Save progress to API - use staff endpoint for staff members
      const profileToSave = mapFormDataToProfile(data);
      // Include physical documents for DG
      const dataWithPhysicalDocs = user?.role === UserRole.DIRECTOR_GENERAL
        ? { ...profileToSave, ...physicalDocs }
        : profileToSave;
      await saveStaffProfileProgress(dataWithPhysicalDocs);

      // Also upload documents to server (not just IndexedDB)
      // This ensures documents are saved even during "save progress"
      try {
        const storedDocs = await getAllDocuments();
        for (const storedDoc of storedDocs) {
          try {
            const file = await storedDocumentToFile(storedDoc);
            await uploadDocumentToServer(storedDoc.type as StudentDocumentType, file);
          } catch {
            // Continue with other documents even if one fails
          }
        }
      } catch {
        // Don't fail the whole save if document upload fails
      }

      // Update local state (but DON'T trigger form reset by keeping same reference pattern)
      setProfileData(prev => ({ ...prev, ...data }));

      // Update user name/email in auth store (reflects in header)
      if (data.prenom || data.nom || data.email) {
        updateUserProfile({
          firstName: data.prenom,
          lastName: data.nom,
          email: data.email,
        });
      }

      setProgressSaved(true);
      setTimeout(() => setProgressSaved(false), 2000);
    } catch (err) {
      console.error('Error saving progress:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setIsSavingProgress(false);
    }
  };

  // Handle profile unlock (admin only)
  const handleUnlockProfile = async () => {
    try {
      await unlockProfile();
      setIsProfileLocked(false);
      setIsEditing(true);
    } catch (err) {
      console.error('Error unlocking profile:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du déverrouillage du profil');
    }
  };

  // Handle StudentProfileForm submission for students
  const handleStudentProfileSubmit = async (data: StudentProfileFormData, _docs: StudentUploadedDocument[]) => {
    setIsSaving(true);
    setError(null);

    try {
      // Map StudentProfileFormData to StudentProfileData for the API
      const profileData: StudentProfileData = {
        // Identity
        nom: data.nom,
        prenom: data.prenom,
        nomArabe: data.nomArabe,
        prenomArabe: data.prenomArabe,
        cnie: data.cnie,
        passport: data.passport,
        sexe: data.sexe,
        dateNaissance: data.dateNaissance,
        paysOrigine: data.paysOrigine,
        handicap: data.handicap,
        // Contact
        email: data.email,
        telephone: data.telephone,
        adresse: data.adresse,
        // Emergency contact
        urgenceNom: data.urgenceNom,
        urgenceRelation: data.urgenceRelation,
        urgenceNumero: data.urgenceNumero,
        urgenceAdresse: data.urgenceAdresse,
        // Academic (only the editable field)
        numeroMassarCNE: data.numeroMassarCNE,
        // Diploma access
        serieBaccalaureat: data.serieBaccalaureat,
        anneeObtentionBac: data.anneeObtentionBac,
        diplomeAcces: data.diplomeAcces,
        specialiteDiplomeAcces: data.specialiteDiplomeAcces,
        mentionDiplomeAcces: data.mentionDiplomeAcces,
        lieuObtentionDiplomeAcces: data.lieuObtentionDiplomeAcces,
        // Status & Aids
        resident: data.resident,
        fonctionnaire: data.fonctionnaire,
        pourcentageBourse: data.pourcentageBourse,
        mobilite: data.mobilite,
      };

      // Save to backend API
      await updateStudentProfile(profileData);

      // Update local state
      setStudentProfileData(data);

      // Update user name/email in auth store
      if (data.prenom || data.nom || data.email) {
        updateUserProfile({
          firstName: data.prenom,
          lastName: data.nom,
          email: data.email,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving student profile:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement du profil étudiant');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle StudentProfileForm save progress
  const handleStudentSaveProgress = async (data: Partial<StudentProfileFormData>, _docs: StudentUploadedDocument[]) => {
    setIsSavingProgress(true);
    setError(null);

    try {
      // Map partial StudentProfileFormData to StudentProfileData for the API
      const profileData: Partial<StudentProfileData> = {
        // Identity
        ...(data.nom && { nom: data.nom }),
        ...(data.prenom && { prenom: data.prenom }),
        ...(data.nomArabe && { nomArabe: data.nomArabe }),
        ...(data.prenomArabe && { prenomArabe: data.prenomArabe }),
        ...(data.cnie && { cnie: data.cnie }),
        ...(data.passport && { passport: data.passport }),
        ...(data.sexe && { sexe: data.sexe }),
        ...(data.dateNaissance && { dateNaissance: data.dateNaissance }),
        ...(data.paysOrigine && { paysOrigine: data.paysOrigine }),
        ...(data.handicap && { handicap: data.handicap }),
        // Contact
        ...(data.email && { email: data.email }),
        ...(data.telephone && { telephone: data.telephone }),
        ...(data.adresse && { adresse: data.adresse }),
        // Emergency contact
        ...(data.urgenceNom && { urgenceNom: data.urgenceNom }),
        ...(data.urgenceRelation && { urgenceRelation: data.urgenceRelation }),
        ...(data.urgenceNumero && { urgenceNumero: data.urgenceNumero }),
        ...(data.urgenceAdresse && { urgenceAdresse: data.urgenceAdresse }),
        // Academic (only the editable field)
        ...(data.numeroMassarCNE && { numeroMassarCNE: data.numeroMassarCNE }),
        // Diploma access
        ...(data.serieBaccalaureat && { serieBaccalaureat: data.serieBaccalaureat }),
        ...(data.anneeObtentionBac && { anneeObtentionBac: data.anneeObtentionBac }),
        ...(data.diplomeAcces && { diplomeAcces: data.diplomeAcces }),
        ...(data.specialiteDiplomeAcces && { specialiteDiplomeAcces: data.specialiteDiplomeAcces }),
        ...(data.mentionDiplomeAcces && { mentionDiplomeAcces: data.mentionDiplomeAcces }),
        ...(data.lieuObtentionDiplomeAcces && { lieuObtentionDiplomeAcces: data.lieuObtentionDiplomeAcces }),
        // Status & Aids
        ...(data.resident !== undefined && { resident: data.resident }),
        ...(data.fonctionnaire !== undefined && { fonctionnaire: data.fonctionnaire }),
        ...(data.pourcentageBourse !== undefined && { pourcentageBourse: data.pourcentageBourse }),
        ...(data.mobilite !== undefined && { mobilite: data.mobilite }),
      };

      // Save to backend API
      await saveStudentProfileProgress(profileData);

      // Update local state
      setStudentProfileData(data as Partial<StudentProfileFormData>);

      // Update user name/email in auth store (reflects in header)
      if (data.prenom || data.nom || data.email) {
        updateUserProfile({
          firstName: data.prenom,
          lastName: data.nom,
          email: data.email,
        });
      }

      setProgressSaved(true);
      setTimeout(() => setProgressSaved(false), 2000);
    } catch (err) {
      console.error('Error saving student progress:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde du progrès');
    } finally {
      setIsSavingProgress(false);
    }
  };

  // Note: Student profile data is now loaded from the backend in the main loadProfile function.
  // localStorage is only used as a cache for saved progress (not yet submitted to backend).

  // Handle ParentProfileForm submission for parents
  const handleParentProfileSubmit = async (data: ParentProfileFormData) => {
    setIsSaving(true);
    setError(null);

    try {
      // Map parent form data to backend format
      // Note: email is NOT sent because it's not in UpdateProfileDto and forbidNonWhitelisted is true
      const backendData = {
        nom: data.nom,
        prenom: data.prenom,
        nomArabe: data.nomArabe,
        prenomArabe: data.prenomArabe,
        sexe: data.sexe,
        telephone: data.telephone,
        phoneCountry: data.phoneCountry,
        adresse: data.adresse,
        relationAvecEtudiant: data.relationAvecEtudiant,
        relationPrecision: data.relationPrecision,
      };

      await updateProfile(backendData);
      setParentProfileData(data);

      // Update user name/email in auth store
      if (data.prenom || data.nom) {
        updateUserProfile({
          firstName: data.prenom,
          lastName: data.nom,
          email: data.email || undefined,
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving parent profile:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement du profil');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle ParentProfileForm save progress
  const handleParentSaveProgress = (data: Partial<ParentProfileFormData>) => {
    setParentProfileData(data);
    // Auto-save to localStorage for parent
    if (user?.id) {
      const parentStorageKey = `parent_profile_${user.id}`;
      localStorage.setItem(parentStorageKey, JSON.stringify(data));
    }
  };

  // Load parent profile data from localStorage only if backend data is empty
  // This is used as a fallback/cache for unsaved progress
  useEffect(() => {
    if (isParent && user?.id) {
      // Only load from localStorage if parentProfileData is empty (backend didn't return data)
      const hasBackendData = parentProfileData.nom || parentProfileData.prenom;
      if (!hasBackendData) {
        const parentStorageKey = `parent_profile_${user.id}`;
        const storedProfile = localStorage.getItem(parentStorageKey);
        if (storedProfile) {
          try {
            const parsed = JSON.parse(storedProfile);
            // Only use localStorage if it has actual data
            if (parsed.nom || parsed.prenom) {
              setParentProfileData(prev => ({ ...prev, ...parsed }));
            }
          } catch (e) {
            console.error('Error parsing stored parent profile:', e);
          }
        }
      }
    }
  }, [isParent, user?.id, parentProfileData.nom, parentProfileData.prenom]);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle document upload with persistence
  const handleDocumentsChange = async (newDocuments: UploadedDocument[]) => {
    // Find current document types
    const currentTypes = documents.map(d => d.type);
    const newTypes = newDocuments.map(d => d.type);

    // Save new/updated documents (only if they have actual content and are not loaded from storage)
    for (const doc of newDocuments) {
      // Check if this is a newly uploaded document (not loaded from IndexedDB)
      // Loaded documents have _originalSize property, newly uploaded ones don't
      const isLoadedFromStorage = '_originalSize' in doc.file;

      // Save if: document has actual content AND is a fresh file (not loaded from storage)
      // This handles both new documents and replaced documents
      const shouldSave = doc.file.size > 0 && !isLoadedFromStorage;

      if (shouldSave) {
        try {
          await saveDocument(doc.type, doc.file);
          console.log('Document saved to IndexedDB:', doc.type, doc.file.name);
        } catch (err) {
          console.error('Error saving document:', err);
        }
      }
    }

    // Delete removed documents
    for (const type of currentTypes) {
      if (!newTypes.includes(type)) {
        try {
          await deleteDocument(type);
        } catch (err) {
          console.error('Error deleting document:', err);
        }
      }
    }

    setDocuments(newDocuments);
  };

  const getRoleName = (role: UserRole): string => {
    const roleNames: Record<UserRole, string> = {
      [UserRole.SUPER_ADMIN]: 'Super Administrateur',
      [UserRole.DIRECTOR_GENERAL]: 'Directeur Général',
      [UserRole.BRANCH_MANAGER]: 'Responsable de Site',
      [UserRole.PEDAGOGICAL_DIRECTOR]: 'Directeur Pédagogique',
      [UserRole.FINANCIAL_MANAGER]: 'Responsable Financier',
      [UserRole.MARKETING_MANAGER]: 'Responsable Marketing',
      [UserRole.COMMERCIAL_MANAGER]: 'Responsable Commercial',
      [UserRole.CALL_CENTER_AGENT]: 'Agent Call Center',
      [UserRole.PROGRAM_HEAD]: 'Chef de Filière',
      [UserRole.ACADEMIC_COORDINATOR]: 'Coordinateur Académique',
      [UserRole.TEACHER]: 'Professeur',
      [UserRole.STUDENT]: 'Étudiant',
      [UserRole.PARENT]: 'Parent',
    };
    return roleNames[role] || role;
  };

  // Check if required documents are uploaded based on user's selected ID document type
  const hasRequiredDocuments = () => {
    if (selectedIdDocType === 'CIN') {
      const hasCINFront = documents.some(d => d.type === 'CIN_FRONT');
      const hasCINBack = documents.some(d => d.type === 'CIN_BACK');
      return hasCINFront && hasCINBack;
    } else {
      return documents.some(d => d.type === 'PASSPORT');
    }
  };

  // Display name from profile data or user
  const displayFirstName = profileData.prenom || user?.firstName || '';
  const displayLastName = profileData.nom || user?.lastName || '';
  const displayEmail = profileData.email || user?.email || '';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Profile Locked Banner - Pending Validation */}
      {isProfileLocked && !canEditLockedProfile && validationStatus?.status === 'PENDING_VALIDATION' && (
        <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-orange-600" />
            <div>
              <span className="text-orange-800 font-medium">Profil en attente de validation</span>
              <p className="text-orange-700 text-sm">
                Votre profil a été soumis et est en cours d&apos;examen. Vous ne pouvez pas le modifier pendant ce temps.
              </p>
            </div>
          </div>
          {/* Signature traceability */}
          {validationStatus.signatureStatus?.reglementInterieurSigned && (
            <div className="mt-3 pt-3 border-t border-orange-200">
              <p className="text-orange-700 text-xs font-medium mb-2">Documents signés électroniquement:</p>
              <div className="text-xs text-gray-600 space-y-1">
                <p>
                  <CheckCircle className="w-3.5 h-3.5 text-green-600 inline mr-1" />
                  <span className="font-medium">Règlement intérieur:</span>{' '}
                  {new Date(validationStatus.signatureStatus.reglementInterieurSignedAt!).toLocaleString('fr-FR', {
                    dateStyle: 'long',
                    timeStyle: 'short',
                  })}
                </p>
                {validationStatus.signatureStatus.consentementMediasSigned && (
                  <p>
                    <CheckCircle className="w-3.5 h-3.5 text-green-600 inline mr-1" />
                    <span className="font-medium">Consentement réseaux sociaux:</span>{' '}
                    {new Date(validationStatus.signatureStatus.consentementMediasSignedAt!).toLocaleString('fr-FR', {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profile Locked Banner - Rejected */}
      {validationStatus?.status === 'REJECTED' && validationStatus.validationRequest && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div>
              <span className="text-red-800 font-medium">Profil rejeté</span>
              <p className="text-red-700 text-sm mt-1">
                {validationStatus.validationRequest.rejectionReason || 'Veuillez corriger les informations et soumettre à nouveau.'}
              </p>
              {validationStatus.validationRequest.rejectionFields && validationStatus.validationRequest.rejectionFields.length > 0 && (
                <p className="text-red-600 text-xs mt-2">
                  <strong>Champs à corriger:</strong> {validationStatus.validationRequest.rejectionFields.join(', ')}
                </p>
              )}
            </div>
          </div>
          {/* Signature traceability - show that documents don't need to be re-signed */}
          {validationStatus.signatureStatus?.reglementInterieurSigned && (
            <div className="mt-3 pt-3 border-t border-red-200">
              <p className="text-red-700 text-xs flex items-center gap-1">
                <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                <span>
                  <strong>Documents déjà signés</strong> - Vous n&apos;avez pas besoin de les re-signer lors de la re-soumission.
                </span>
              </p>
              <div className="mt-2 text-xs text-gray-600 space-y-1">
                <p>
                  <span className="font-medium">Règlement intérieur signé le:</span>{' '}
                  {new Date(validationStatus.signatureStatus.reglementInterieurSignedAt!).toLocaleString('fr-FR', {
                    dateStyle: 'long',
                    timeStyle: 'short',
                  })}
                </p>
                {validationStatus.signatureStatus.consentementMediasSigned && (
                  <p>
                    <span className="font-medium">Consentement réseaux sociaux signé le:</span>{' '}
                    {new Date(validationStatus.signatureStatus.consentementMediasSignedAt!).toLocaleString('fr-FR', {
                      dateStyle: 'long',
                      timeStyle: 'short',
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Profile Locked Banner - Generic (when validation status is not available) */}
      {isProfileLocked && !canEditLockedProfile && !validationStatus && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800">
            Votre profil a été soumis et ne peut plus être modifié. Contactez l'administration si vous avez besoin de faire des modifications.
          </span>
        </div>
      )}

      {/* Parent Created By Staff - View Only Banner */}
      {isParent && isParentCreatedByStaff && !canEditLockedProfile && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
          <Lock className="w-5 h-5 text-blue-600" />
          <span className="text-blue-800">
            Votre profil a été créé par l'administration et est en mode consultation uniquement. Si vous souhaitez modifier vos informations, veuillez contacter l'établissement.
          </span>
        </div>
      )}

      {/* Admin can unlock */}
      {isProfileLocked && canEditLockedProfile && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-blue-600" />
            <span className="text-blue-800">
              Ce profil est verrouillé. En tant qu'administrateur, vous pouvez le déverrouiller pour permettre des modifications.
            </span>
          </div>
          <button
            onClick={handleUnlockProfile}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Unlock className="w-4 h-4" />
            Déverrouiller
          </button>
        </div>
      )}

      {/* Success Message */}
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">Profil enregistré avec succès!</span>
        </div>
      )}

      {/* Submitted Message */}
      {submittedMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{submittedMessage}</span>
        </div>
      )}

      {/* Progress Saved Message */}
      {progressSaved && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          <span className="text-blue-800">Progrès sauvegardé!</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Profile Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Profile Photo */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-primary-600" />
              )}
            </div>
            {(!isProfileLocked || canEditLockedProfile) && (
              <label className="absolute bottom-0 right-0 p-1.5 bg-primary-600 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                <Camera className="w-4 h-4 text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* User Info - Shows profile data */}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {displayFirstName} {displayLastName}
            </h1>
            <p className="text-gray-500">{displayEmail}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                {user?.role && getRoleName(user.role)}
              </span>
              {/* Show student code for students, staff code for staff */}
              {isStudent && studentCode ? (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-mono">
                  {studentCode}
                </span>
              ) : staffCode && !isStudent && !isParent && (
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-mono">
                  {staffCode}
                </span>
              )}
              {/* Validation Status Badge */}
              {validationStatus?.status === 'PENDING_VALIDATION' && (
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  En attente de validation
                </span>
              )}
              {validationStatus?.status === 'APPROVED' && (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Profil validé
                </span>
              )}
              {validationStatus?.status === 'REJECTED' && (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Profil rejeté
                </span>
              )}
              {isProfileLocked && !validationStatus && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Profil soumis
                </span>
              )}
            </div>
            {/* Campus Affiliation for Staff (except DG who has global access) */}
            {!isStudent && !isParent && user?.role !== UserRole.DIRECTOR_GENERAL && staffCampuses.length > 0 && (
              <div className="mt-3 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Site(s) d'affectation</p>
                  <div className="flex flex-wrap gap-2">
                    {staffCampuses.map((campusAffil) => (
                      <span
                        key={campusAffil.id}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm ${
                          campusAffil.isPrimary
                            ? 'bg-blue-100 text-blue-800 font-medium'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {campusAffil.campus.name}
                        {campusAffil.campus.city && (
                          <span className="text-xs opacity-75">({campusAffil.campus.city})</span>
                        )}
                        {campusAffil.isPrimary && staffCampuses.length > 1 && (
                          <span className="text-xs bg-blue-200 text-blue-800 px-1 rounded">Principal</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {/* Academic Year for Staff */}
            {!isStudent && !isParent && userAcademicYear && (
              <div className="mt-3 flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Année académique</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm ${
                    userAcademicYear.isCurrent
                      ? 'bg-green-100 text-green-800 font-medium'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {userAcademicYear.name}
                    {userAcademicYear.isCurrent && (
                      <span className="text-xs bg-green-200 text-green-800 px-1 rounded">En cours</span>
                    )}
                  </span>
                </div>
              </div>
            )}
            {/* Campus for Students */}
            {isStudent && studentCampus && (
              <div className="mt-3 flex items-start gap-2">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Campus</p>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm bg-blue-100 text-blue-800 font-medium">
                    {studentCampus.name}
                    {studentCampus.city && (
                      <span className="text-xs opacity-75">({studentCampus.city})</span>
                    )}
                  </span>
                </div>
              </div>
            )}
            {/* Academic Year for Students */}
            {isStudent && studentAcademicYear && (
              <div className="mt-3 flex items-start gap-2">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 mb-1">Année académique d&apos;inscription</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm ${
                    studentAcademicYear.isCurrent
                      ? 'bg-green-100 text-green-800 font-medium'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {studentAcademicYear.name}
                    {studentAcademicYear.isCurrent && (
                      <span className="text-xs bg-green-200 text-green-800 px-1 rounded">En cours</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Edit Button - Always show for DG/DP (canOnlySave), or for others when profile not locked */}
          {!isEditing && (!isProfileLocked || canOnlySave || canEditLockedProfile) && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" />
              Modifier le profil
            </button>
          )}
        </div>
      </div>

      {/* Student Profile Form - Full 7-section form for students */}
      {isStudent && isEditing && (
        <StudentProfileForm
          ref={studentFormRef}
          initialData={studentProfileData}
          onSubmit={handleStudentProfileSubmit}
          onSaveProgress={handleStudentSaveProgress}
          isLoading={isSaving}
          isLocked={isProfileLocked && !canEditLockedProfile}
          campus={studentCampus}
        />
      )}

      {/* Parent Profile Form - Simplified form for parents/guardians */}
      {/* View-only mode if: profile locked OR parent was created by staff (and not admin) */}
      {isParent && isEditing && (
        <ParentProfileForm
          ref={parentFormRef}
          initialData={parentProfileData}
          onSubmit={handleParentProfileSubmit}
          onSaveProgress={handleParentSaveProgress}
          isLoading={isSaving}
          isLocked={(isProfileLocked || isParentCreatedByStaff) && !canEditLockedProfile}
        />
      )}

      {/* Super Admin Simplified Profile - Only name and email fields with inline edit */}
      {isSuperAdmin && (
        <SuperAdminProfileSection
          nom={profileData.nom || ''}
          prenom={profileData.prenom || ''}
          email={profileData.email || user?.email || ''}
          onSave={async (data) => {
            setIsSaving(true);
            try {
              await updateProfile({ nom: data.nom, prenom: data.prenom, email: data.email });
              setProfileData(prev => ({ ...prev, nom: data.nom, prenom: data.prenom, email: data.email }));
              updateUserProfile({ firstName: data.prenom, lastName: data.nom, email: data.email });
              setSaveSuccess(true);
              setTimeout(() => setSaveSuccess(false), 3000);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
            } finally {
              setIsSaving(false);
            }
          }}
          isSaving={isSaving}
        />
      )}

      {/* Staff Profile Form - For all staff roles (not students, not parents, not super admin) */}
      {!isStudent && !isParent && !isSuperAdmin && isEditing && profileLoaded && (
        <>
          <ProfileForm
            ref={formRef}
            initialData={profileData}
            onSubmit={handleSubmit}
            onSaveProgress={handleSaveProgress}
            onCancel={() => setIsEditing(false)}
            isLoading={isSaving}
            isSavingProgress={isSavingProgress}
            progressSaved={progressSaved}
            userRole={user?.role}
            onCountryChange={handleCountryChange}
            showActions={false}
            onProfessorFieldsChange={handleProfessorFieldsChange}
            onIsAlsoProfessorChange={handleIsAlsoProfessorChange}
            departements={departements}
            campuses={campuses}
            authToken={localStorage.getItem('nexedu_access_token') || undefined}
          />

          {/* Documents Section - Only for staff */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600" />
              Documents
              {hasRequiredDocuments() && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </h2>

            <DocumentUpload
              onDocumentsChange={handleDocumentsChange}
              onIdDocumentTypeChange={handleIdDocumentTypeChange}
              initialIdDocumentType={selectedIdDocType}
              isStudent={isStudent}
              isStaff={isStaff}
              isProfesseur={showProfessorDocuments}
              isVacataire={isVacataire}
              isFromPublicInstitution={isFromPublicInstitution}
              currentAcademicYear={currentAcademicYear || undefined}
              engagementTemplateUrl={engagementTemplateUrl || undefined}
              existingDocuments={documents}
              makeAllOptional={user?.role === UserRole.DIRECTOR_GENERAL}
            />
          </div>

          {/* Physical Documents Tracking Section - Only for DG */}
          {user?.role === UserRole.DIRECTOR_GENERAL && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-emerald-600" />
                Documents physiques
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Cochez les documents physiques que vous avez remis a l'administration.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocCin}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocCin: !prev.physicalDocCin }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">CIN (Carte d'identite nationale)</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocPassport}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocPassport: !prev.physicalDocPassport }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Passeport</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocCv}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocCv: !prev.physicalDocCv }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">CV (Curriculum Vitae)</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocContratTravail}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocContratTravail: !prev.physicalDocContratTravail }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Contrat de travail</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocDiplome}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocDiplome: !prev.physicalDocDiplome }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Diplome</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocAttestationRib}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocAttestationRib: !prev.physicalDocAttestationRib }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Attestation RIB</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocReglementInterieur}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocReglementInterieur: !prev.physicalDocReglementInterieur }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Reglement interieur signe</span>
                </label>
                <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={physicalDocs.physicalDocConsentement}
                    onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocConsentement: !prev.physicalDocConsentement }))}
                    className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-700">Consentement medias</span>
                </label>
                {/* Engagement professeur - only if DG also teaches */}
                {isAlsoProfessor && (
                  <label className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer md:col-span-2">
                    <input
                      type="checkbox"
                      checked={physicalDocs.physicalDocEngagementProfesseur}
                      onChange={() => setPhysicalDocs(prev => ({ ...prev, physicalDocEngagementProfesseur: !prev.physicalDocEngagementProfesseur }))}
                      className="w-5 h-5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                    />
                    <div className="flex-1">
                      <span className="text-sm text-amber-900">
                        Engagement des professeurs{currentAcademicYear ? ` (${currentAcademicYear})` : ''}
                      </span>
                      <p className="text-xs text-amber-600 mt-0.5">Document a renouveler chaque annee academique</p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Form Actions - For Parents */}
      {/* Hide actions entirely if parent was created by staff (view-only mode) */}
      {isEditing && isParent && !(isParentCreatedByStaff && !canEditLockedProfile) && (
        <div className="flex flex-wrap gap-3 pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <X className="w-4 h-4" />
            Annuler
          </button>

          {/* Submit Profile Button for Parents */}
          {!isProfileLocked && (
            <button
              type="button"
              onClick={() => {
                if (parentFormRef.current) {
                  parentFormRef.current.submit();
                }
              }}
              disabled={isSaving || isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Enregistrer le profil
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Form Actions - For Staff (not students, not parents, not super admin) */}
      {isEditing && !isStudent && !isParent && !isSuperAdmin && (
        <div className="flex flex-wrap gap-3 pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <X className="w-4 h-4" />
            Annuler
          </button>

          <button
            type="button"
            onClick={() => {
              if (formRef.current) {
                const data = formRef.current.getFormValues();
                handleSaveProgress(data);
              }
            }}
            disabled={isSaving || isSavingProgress || progressSaved || isSubmitting}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg disabled:cursor-not-allowed transition-colors ${
              progressSaved
                ? 'border-green-300 text-green-700 bg-green-50'
                : 'border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50'
            }`}
          >
            {isSavingProgress ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : progressSaved ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Progrès sauvegardé
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                Sauvegarder le progrès
              </>
            )}
          </button>

          {/* Submit Profile Button - for non-student users who must submit */}
          {!isProfileLocked && mustSubmitProfile && user?.role && (
            <ProfileSubmissionFlow
              userRole={user.role}
              onBeforeSubmit={async () => {
                // Save the staff profile data BEFORE submission
                if (formRef.current) {
                  const data = formRef.current.getFormValues();
                  const profileToSave = mapFormDataToProfile(data as CommonProfileFields);
                  await updateProfile(profileToSave);
                }
              }}
              onSubmissionComplete={() => {
                // Update UI AFTER successful submission
                setIsProfileLocked(true);
                setIsEditing(false);
                setSubmittedMessage('Votre profil a été soumis avec succès et est en attente de validation.');
                setTimeout(() => setSubmittedMessage(null), 5000);
                // Update the auth store to reflect the new status
                updateProfileSubmissionStatus('PENDING_VALIDATION');
                // Refresh validation status
                getValidationStatus().then(setValidationStatus).catch(() => {});
              }}
              onError={setError}
              isDisabled={isSaving || isSavingProgress || isSubmitting}
              isProfileComplete={hasRequiredDocuments()}
              signatureStatus={validationStatus?.signatureStatus}
              isResubmission={validationStatus?.isResubmission}
            />
          )}

          {/* Save button - only for DG and Directeur Pédagogique */}
          {canOnlySave && (
            <button
              type="button"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submitForm();
                }
              }}
              disabled={isSaving || isSavingProgress || isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Enregistrer le profil
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Form Actions for Students */}
      {isEditing && isStudent && (
        <div className="flex items-center gap-3 pt-6 mt-6 border-t border-gray-200">
          {/* Annuler - Red */}
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-300 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
            Annuler
          </button>

          {/* Sauvegarder le progrès */}
          <button
            type="button"
            onClick={() => {
              if (studentFormRef.current) {
                studentFormRef.current.saveProgress();
              }
            }}
            disabled={isSaving || isSavingProgress || progressSaved || isSubmitting}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
              progressSaved
                ? 'border-green-300 text-green-700 bg-green-50'
                : 'border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            {isSavingProgress ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : progressSaved ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Progrès sauvegardé
              </>
            ) : (
              <>
                <Clock className="w-4 h-4" />
                Sauvegarder le progrès
              </>
            )}
          </button>

          {/* Enregistrer et Soumettre - Uses ProfileSubmissionFlow for signature modals */}
          {!isProfileLocked && user?.role && (
            <ProfileSubmissionFlow
              userRole={user.role}
              onBeforeSubmit={async () => {
                // Save the student profile data BEFORE submission
                if (studentFormRef.current) {
                  const data = studentFormRef.current.getFormValues();
                  const docs = studentFormRef.current.getDocuments();
                  await handleStudentProfileSubmit(data as StudentProfileFormData, docs);
                }
              }}
              onSubmissionComplete={() => {
                // Update UI AFTER successful submission
                setIsProfileLocked(true);
                setIsEditing(false);
                setSubmittedMessage('Votre profil a été soumis avec succès et est en attente de validation.');
                setTimeout(() => setSubmittedMessage(null), 5000);
                // Update the auth store to reflect the new status
                updateProfileSubmissionStatus('PENDING_VALIDATION');
                // Refresh validation status
                getValidationStatus().then(setValidationStatus).catch(() => {});
              }}
              onError={setError}
              isDisabled={isSaving || isSavingProgress || isSubmitting}
              validateBeforeStart={async () =>
                studentFormRef.current ? studentFormRef.current.validateForSubmission() : []
              }
              buttonLabel="Enregistrer et Soumettre"
              signatureStatus={validationStatus?.signatureStatus}
              isResubmission={validationStatus?.isResubmission}
            />
          )}
        </div>
      )}

      {/* Display-only Profile View for Students with Validated Profile */}
      {!isEditing && isStudent && isProfileLocked && !canEditLockedProfile && validationStatus?.status === 'APPROVED' && (
        <StudentProfileView
          data={studentProfileData}
          campus={studentCampus}
          showChangeRequest={true}
          signatureStatus={validationStatus?.signatureStatus}
          documents={documents.map(doc => ({
            type: doc.type,
            previewUrl: doc.previewUrl || '',
          }))}
        />
      )}

      {/* Display-only Profile View for Staff with Validated Profile */}
      {!isEditing && !isStudent && !isParent && !isSuperAdmin && isProfileLocked && !canEditLockedProfile && validationStatus?.status === 'APPROVED' && (
        <StaffProfileView
          data={profileData}
          showChangeRequest={true}
          isProfessor={user?.role === UserRole.TEACHER}
          signatureStatus={validationStatus?.signatureStatus}
          documents={documents.map(doc => ({
            id: doc.type,
            documentType: doc.type,
            fileName: doc.file.name,
            fileUrl: doc.previewUrl || '',
            isVerified: false,
          }))}
        />
      )}

      {/* Display-only Profile View for DG and Directeur Pédagogique (canOnlySave roles) */}
      {!isEditing && !isStudent && !isParent && !isSuperAdmin && canOnlySave && (
        <>
          <StaffProfileView
            data={profileData}
            showChangeRequest={false}
            isProfessor={user?.role === UserRole.TEACHER || localIsAlsoProfessor === true}
            departementName={departements.find(d => d.id === (profileData as Record<string, unknown>).departementAttacheId)?.labelFr}
            gradeName={grades.find(g => g.id === (profileData as Record<string, unknown>).gradeId)?.labelFr}
            diplomeName={diplomes.find(d => d.id === (profileData as Record<string, unknown>).diplomeId)?.labelFr}
            payrollCampusName={campuses.find(c => c.id === (profileData as Record<string, unknown>).payrollCampusId)?.name}
            documents={documents.map(doc => ({
              id: doc.type,
              documentType: doc.type,
              fileName: doc.file.name,
              fileUrl: doc.previewUrl || '',
              isVerified: false,
            }))}
          />

          {/* Physical Documents View for DG (read-only in view mode) */}
          {user?.role === UserRole.DIRECTOR_GENERAL && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-emerald-600" />
                Documents physiques
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocCin ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocCin ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocCin && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocCin ? 'text-emerald-700' : 'text-gray-500'}`}>CIN (Carte d'identite nationale)</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocPassport ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocPassport ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocPassport && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocPassport ? 'text-emerald-700' : 'text-gray-500'}`}>Passeport</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocCv ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocCv ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocCv && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocCv ? 'text-emerald-700' : 'text-gray-500'}`}>CV (Curriculum Vitae)</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocContratTravail ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocContratTravail ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocContratTravail && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocContratTravail ? 'text-emerald-700' : 'text-gray-500'}`}>Contrat de travail</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocDiplome ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocDiplome ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocDiplome && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocDiplome ? 'text-emerald-700' : 'text-gray-500'}`}>Diplome</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocAttestationRib ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocAttestationRib ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocAttestationRib && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocAttestationRib ? 'text-emerald-700' : 'text-gray-500'}`}>Attestation RIB</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocReglementInterieur ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocReglementInterieur ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocReglementInterieur && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocReglementInterieur ? 'text-emerald-700' : 'text-gray-500'}`}>Reglement interieur signe</span>
                </div>
                <div className={`flex items-center gap-3 p-3 border rounded-lg ${physicalDocs.physicalDocConsentement ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocConsentement ? 'bg-emerald-600 text-white' : 'bg-gray-300'}`}>
                    {physicalDocs.physicalDocConsentement && <CheckCircle className="w-4 h-4" />}
                  </div>
                  <span className={`text-sm ${physicalDocs.physicalDocConsentement ? 'text-emerald-700' : 'text-gray-500'}`}>Consentement medias</span>
                </div>
                {isAlsoProfessor && (
                  <div className={`flex items-center gap-3 p-3 border rounded-lg md:col-span-2 ${physicalDocs.physicalDocEngagementProfesseur ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${physicalDocs.physicalDocEngagementProfesseur ? 'bg-amber-600 text-white' : 'bg-gray-300'}`}>
                      {physicalDocs.physicalDocEngagementProfesseur && <CheckCircle className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <span className={`text-sm ${physicalDocs.physicalDocEngagementProfesseur ? 'text-amber-700' : 'text-gray-500'}`}>
                        Engagement des professeurs{currentAcademicYear ? ` (${currentAcademicYear})` : ''}
                      </span>
                      <p className="text-xs text-amber-500 mt-0.5">Document a renouveler chaque annee academique</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Display-only Profile View for other staff cases (not DG/DP) */}
      {!isEditing &&
       !canOnlySave &&
       !(isStudent && isProfileLocked && !canEditLockedProfile && validationStatus?.status === 'APPROVED') &&
       !(!isStudent && !isParent && !isSuperAdmin && isProfileLocked && !canEditLockedProfile && validationStatus?.status === 'APPROVED') && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Informations du profil
          </h2>
          {isProfileLocked && !canEditLockedProfile ? (
            <p className="text-gray-500">
              Votre profil a été soumis et est en cours de validation.
            </p>
          ) : (
            <p className="text-gray-500">
              Cliquez sur "Modifier le profil" pour mettre à jour vos informations.
            </p>
          )}
        </div>
      )}

    </div>
  );
};

export default ProfilePage;
